import { useState } from "react"
import { CalendarIcon } from "lucide-react"

import { Calendar } from "./Calendar"
import { Input } from "./Input"
import { Popover, PopoverContent, PopoverTrigger } from "./Popover"
import { cn } from "../lib/cn"

type Props = {
  id?: string
  name?: string
  valor: string
  aoMudar: (valor: string) => void
  rotulo?: string
  obrigatorio?: boolean
  desabilitado?: boolean
  min?: string
  max?: string
  classeDaCaixa?: string
  placeholder?: string
}

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

function paraTela(iso: string): string {
  const partes = ISO.exec(iso)
  return partes === null ? "" : `${partes[3]}/${partes[2]}/${partes[1]}`
}

function comMascara(texto: string): string {
  const d = texto.replace(/\D/g, "").slice(0, 8)
  if (d.length <= 2) return d
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`
}

function paraIso(tela: string): string | null {
  const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(tela)
  if (partes === null) return null
  const [, dia, mes, ano] = partes
  const data = new Date(Number(ano), Number(mes) - 1, Number(dia))
  if (data.getFullYear() !== Number(ano) || data.getMonth() !== Number(mes) - 1 || data.getDate() !== Number(dia)) return null
  return `${ano}-${mes}-${dia}`
}

function paraData(iso: string): Date | undefined {
  const partes = ISO.exec(iso)
  return partes === null ? undefined : new Date(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3]))
}

function deData(data: Date): string {
  const mes = String(data.getMonth() + 1).padStart(2, "0")
  const dia = String(data.getDate()).padStart(2, "0")
  return `${data.getFullYear()}-${mes}-${dia}`
}

function foraDoLimite(iso: string, min?: string, max?: string): boolean {
  return (min !== undefined && min !== "" && iso < min) || (max !== undefined && max !== "" && iso > max)
}

export function CampoDeData({
  id,
  name,
  valor,
  aoMudar,
  rotulo,
  obrigatorio = false,
  desabilitado = false,
  min,
  max,
  classeDaCaixa,
  placeholder = "Digite ou escolha a data",
}: Props) {
  const [texto, definirTexto] = useState(paraTela(valor))
  const [valorAnterior, definirValorAnterior] = useState(valor)
  const [aberto, definirAberto] = useState(false)

  if (valor !== valorAnterior) {
    definirValorAnterior(valor)
    if (paraIso(texto) !== valor) definirTexto(paraTela(valor))
  }

  const iso = paraIso(texto)
  const invalido = texto !== "" && (iso === null ? texto.length === 10 : foraDoLimite(iso, min, max))

  const digitar = (bruto: string) => {
    const novo = comMascara(bruto)
    definirTexto(novo)
    if (novo === "") {
      aoMudar("")
      return
    }
    const convertido = paraIso(novo)
    if (convertido !== null && !foraDoLimite(convertido, min, max)) aoMudar(convertido)
  }

  const desabilitarDia = (dia: Date) => foraDoLimite(deData(dia), min, max)

  return (
    <div data-slot="campo-de-data" className={cn("relative flex w-full min-w-0 items-center", classeDaCaixa)}>
      <Input
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        data-form-type="other"
        maiusculas={false}
        placeholder={placeholder}
        aria-label={rotulo}
        aria-required={obrigatorio ? "true" : undefined}
        required={obrigatorio}
        aria-invalid={invalido || undefined}
        disabled={desabilitado}
        maxLength={10}
        className="pr-10 tabular-nums"
        value={texto}
        onChange={(evento) => digitar(evento.target.value)}
      />
      <Popover open={aberto} onOpenChange={abrirSePuder(desabilitado, definirAberto)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={desabilitado}
            aria-label="Abrir o calendário"
            title="Escolhe a data no calendário"
            className="absolute right-1 inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CalendarIcon className="size-4" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={paraData(iso ?? valor)}
            defaultMonth={paraData(iso ?? valor)}
            disabled={min !== undefined || max !== undefined ? desabilitarDia : undefined}
            onSelect={(dia) => {
              if (dia === undefined) return
              const escolhido = deData(dia)
              definirTexto(paraTela(escolhido))
              aoMudar(escolhido)
              definirAberto(false)
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

function abrirSePuder(desabilitado: boolean, definir: (aberto: boolean) => void) {
  return (aberto: boolean) => {
    if (!desabilitado) definir(aberto)
  }
}
